import { useId, useState } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Icon } from "../../design/components/Icon";
import "../../design/components/select.css";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { opportunityScopeOf, useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { WhatsAppButton } from "../../whatsapp/WhatsAppButton";
import { WhatsAppDialog } from "../../whatsapp/WhatsAppDialog";
import { formatPhoneForDisplay, statusTone } from "../lead-display";
import { ArchiveButton } from "../archive/ArchiveButton";
import { isCardLevelError, leadStatusesQuery, useLeadView, type CardLead, type LeadCardWrites } from "../lead-queries";
import { BookAppointmentDialog } from "../../appointments/BookAppointmentDialog";
import { CallbackDialog, callsBlocked, canCallBack, canSetCallback } from "./CallbackDialog";
import { CallButton } from "./CallButton";
import { holderNames, mainPhone, statusEdit } from "./card-fields";
import { LeadHeaderMore, type HeaderAction } from "./LeadHeaderMore";
import { canRegisterCall, ManualCallDialog } from "./ManualCallDialog";
import { TagsDialog } from "./TagsDialog";

// La testata della scheda (piano M2 Task 18, mock-up `scheda-lead.html`): «Lead», il nome (senza nome e cognome il
// telefono principale, come nel mock-up; con due titolari tutti e due), il salone, «Owner: …», il menu dello status
// (soltanto gli status che si scelgono) e i tag. Il telefono e la città non stanno qui: sono nei dati (decisione del 25
// settembre 2026). Dei sette pulsanti confermati, nel loro ordine (Chiama, WhatsApp, Nota, Richiamo, Prenota disco o demo,
// Opportunità, Registra attività: decisione del 25 settembre 2026, `PRODUCT_SPEC.md` §6), ci sono «Chiama» (piano M4, Task 13:
// spento con il motivo quando non si chiama, `CallButton.tsx`), «Nota», che porta al riquadro della nota nella Timeline,
// «Richiamo» (piano M5, Task 17: la finestra «Richiama dopo» senza chiamata, o «Sposta» del richiamo aperto; non c'è quando le
// chiamate al Lead sono ferme, decisione del 25 settembre 2026, né per chi non può fissare richiami) e «Registra attività» (piano
// M5, Task 19: la telefonata fatta fuori dal CRM, `ManualCallDialog.tsx`, per chi chiama e modifica il Lead) e «WhatsApp» (piano M6,
// R1, Task 4a: subito dopo «Chiama», spento con il perché, `WhatsAppButton.tsx`, e la sua finestra a mano); «Prenota disco o demo» (piano M6, R7, Task 6) dopo «Richiamo»;
// «Opportunità» (LEAD-2, R8) dopo di lui, per chi ha l'area `opportunities` (negata per difetto, come il riquadro): apre la finestra
// «Opportunità di …» della scheda, la stessa di «Apri l'opportunità» (`OpportunityDialog.tsx`, tenuta da `LeadCardPage.tsx`).
// «Chiama» e «WhatsApp» restano sempre in vista; gli altri, quando la scheda è più
// stretta delle sue tre aree (accanto alla rail fissa a 1280 px), passano sotto «Altro» nel loro ordine e il nome del Lead tiene
// almeno 200 px (ruling A4, questione 390; le regole in `lead-card.css`, la prova in `layout-width.test.ts` e nell'end-to-end).
// «Archivia» (piano M3 Task 12), per chi ha `permissions.canArchive`, sta nella riga dei fatti accanto all'owner, come nel
// mock-up rivisto dal product owner (decisione P36): la domanda si apre lì, in riga. Per questo la riga è un `div`.

/** Il nome della scheda: i titolari, oppure il telefono principale. */
export function cardTitle(t: Translate, lead: CardLead): string {
  const { first, second } = holderNames(lead);
  if (first && second) return t("leadCard.twoHolders", { first, second });
  if (first) return first;
  const phone = mainPhone(lead);
  return phone ? formatPhoneForDisplay(phone) : "—";
}

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  writes: LeadCardWrites;
  onCardError: (error: unknown) => void;
  onNote: () => void;
  /** «Opportunità»: la finestra «Opportunità di …» della scheda. */
  onOpportunity: () => void;
  /** «Archivia» è riuscito: la scheda torna all'elenco. */
  onArchived: () => void;
};

export function LeadHeader({ tab, card, writes, onCardError, onNote, onOpportunity, onArchived }: Props) {
  const t = useT();
  const toast = useToast();
  const statusId = useId();
  const statuses = useQuery(leadStatusesQuery(tab));
  const access = useAccess();
  const view = useLeadView();
  const [editingTags, setEditingTags] = useState(false);
  const [callingBack, setCallingBack] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [writing, setWriting] = useState(false);
  const [booking, setBooking] = useState(false);
  const { lead, permissions } = card;
  // «Richiamo» fissa un richiamo, oppure sposta quello aperto: serve il permesso di quello che farà.
  const offerCallback = !callsBlocked(card) && canCallBack(access, card, view);
  // Le azioni dopo «Chiama» e «WhatsApp», nell'ordine della testata: in riga, oppure sotto «Altro» quando non ci stanno.
  const extras: HeaderAction[] = [];
  if (permissions.canAddNotes) extras.push({ key: "note", label: t("leadCard.note"), icon: "note", onSelect: onNote });
  if (offerCallback) extras.push({ key: "callback", label: t("callbacks.headerButton"), icon: "clock", opensDialog: true, onSelect: () => setCallingBack(true) });
  // «Prenota disco o demo» (piano M6, R7, Task 6): chi fissa Task su questo Lead, anche con le chiamate ferme; il server decide.
  if (canSetCallback(access, card, view)) extras.push({ key: "appointment", label: t("appointments.headerButton"), icon: "calendar", opensDialog: true, onSelect: () => setBooking(true) });
  if (opportunityScopeOf(access, "view") !== "none") extras.push({ key: "opportunity", label: t("opportunities.headerButton"), icon: "briefcase", opensDialog: true, onSelect: onOpportunity });
  if (canRegisterCall(access, card)) extras.push({ key: "manual-call", label: t("manualCall.button"), icon: "note", opensDialog: true, onSelect: () => setRegistering(true) });

  // Lo status di adesso resta nel menu anche se non si sceglie più (per esempio uno importato da Close), spento.
  const choices = statuses.data ?? [];
  const current = choices.some((status) => status.id === lead.status.id) ? null : lead.status;

  function changeStatus(id: string) {
    const status = choices.find((candidate) => candidate.id === id);
    if (!status || status.id === lead.status.id) return;
    writes.save(statusEdit(status)).then(
      () => toast.show(t("leadCard.statusSaved", { name: status.name }), "success"),
      (error: unknown) => {
        if (isCardLevelError(error)) onCardError(error);
        else toast.show(messageForError(t, error), "warning");
      },
    );
  }

  return (
    <header className="g-lead-head">
      <div className="g-lead-head__identity">
        <p className="g-lead-head__eyebrow">{t("leadCard.eyebrow")}</p>
        <h1 className="g-lead-head__title">{cardTitle(t, lead)}</h1>
        <div className="g-lead-head__facts">
          {lead.salonName ? <strong>{lead.salonName}</strong> : null}
          <span className="g-lead-head__fact">
            <Icon name="user" />
            {lead.owner ? t("leadCard.ownerFact", { name: lead.owner.displayName }) : t("leadCard.ownerFact", { name: t("leadCard.noOwner") })}
          </span>
          {permissions.canArchive ? <ArchiveButton tab={tab} lead={lead} onArchived={onArchived} /> : null}
        </div>
        <div className="g-lead-head__facts">
          <span className="g-lead-head__status" data-tone={statusTone(lead.status.name)}>
            <label htmlFor={statusId}>{t("leadCard.status")}</label>
            <select
              id={statusId}
              className="g-select"
              value={lead.status.id}
              disabled={!permissions.canUpdate || !statuses.isSuccess}
              onChange={(event) => changeStatus(event.target.value)}
            >
              {current || !statuses.isSuccess ? (
                <option value={lead.status.id} disabled={statuses.isSuccess}>
                  {lead.status.name}
                </option>
              ) : null}
              {choices.map((status) => (
                <option key={status.id} value={status.id}>{status.name}</option>
              ))}
            </select>
          </span>
          <span className="g-lead-head__tags" role="group" aria-label={t("leadCard.tags")}>
            {lead.tags.length > 0 ? (
              lead.tags.map((tag) => (
                <Chip key={tag.id} icon="tag">
                  {tag.name}
                </Chip>
              ))
            ) : (
              <span className="g-lead-head__none">{t("leadCard.noTags")}</span>
            )}
            {permissions.canManageTags ? (
              <Button variant="ghost" className="g-lead-head__tag-edit" aria-haspopup="dialog" onClick={() => setEditingTags(true)}>
                <Icon name="tag" />
                {t("leadCard.editTags")}
              </Button>
            ) : null}
          </span>
        </div>
      </div>
      <div className="g-lead-head__actions" role="group" aria-label={t("leadCard.actions")}>
        <CallButton tab={tab} card={card} leadName={cardTitle(t, lead)} />
        <WhatsAppButton tab={tab} card={card} onOpen={() => setWriting(true)} />
        {extras.map((action) => (
          <Button key={action.key} className="g-lead-head__extra" aria-haspopup={action.opensDialog ? "dialog" : undefined} onClick={action.onSelect}>
            <Icon name={action.icon} />
            {action.label}
          </Button>
        ))}
        {extras.length > 0 ? <LeadHeaderMore actions={extras} /> : null}
      </div>
      {editingTags ? <TagsDialog tab={tab} lead={lead} canCreate={permissions.canManageTags} onSubmit={writes.setTags} onClose={() => setEditingTags(false)} /> : null}
      {callingBack ? <CallbackDialog tab={tab} card={card} timeZone={access.context.settings.timeZone} onClose={() => setCallingBack(false)} /> : null}
      {registering ? <ManualCallDialog tab={tab} card={card} timeZone={access.context.settings.timeZone} onClose={() => setRegistering(false)} /> : null}
      {writing ? <WhatsAppDialog tab={tab} leadId={lead.id} onClose={() => setWriting(false)} /> : null}
      {booking ? <BookAppointmentDialog tab={tab} leadId={lead.id} timeZone={access.context.settings.timeZone} onClose={() => setBooking(false)} /> : null}
    </header>
  );
}
